import React, {useState, useEffect, useMemo, useCallback} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Input,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const AmountSelector = ({setAmount}) => {
    const configs = getConfig()
    const [selectedAmountOption, setSelectedAmountOption] = useState(null)
    const [customAmountValue, setCustomAmountValue] = useState('')
    const giftCardMinAmount = configs.app.library.giftCardMinAmount
    const giftCardMaxAmount = configs.app.library.giftCardMaxAmount
    const presetAmounts = configs.app.library.presetAmounts
        .split(',')
        .map((amount) => amount.trim())
    const isCustomAmountSelected = selectedAmountOption === 'custom'

    const {parsedCustomAmount, isBelowMinimum, isAboveMaximum, isAmountValid} = useMemo(() => {
        const parsedValue = Number(customAmountValue)
        const isNumeric = !isNaN(parsedValue) && isFinite(parsedValue)
        const belowMin = isCustomAmountSelected && isNumeric && parsedValue < giftCardMinAmount
        const aboveMax = isCustomAmountSelected && isNumeric && parsedValue > giftCardMaxAmount
        return {
            parsedCustomAmount: isNumeric ? parsedValue : null,
            isBelowMinimum: belowMin,
            isAboveMaximum: aboveMax,
            isAmountValid: !isCustomAmountSelected || (isNumeric && !belowMin && !aboveMax)
        }
    }, [customAmountValue, isCustomAmountSelected, giftCardMinAmount, giftCardMaxAmount])

    const finalDisplayAmount = isAmountValid
        ? isCustomAmountSelected
            ? parsedCustomAmount
            : selectedAmountOption
        : 0

    const handlePresetAmountClick = useCallback((value) => {
        setSelectedAmountOption(value)
        setCustomAmountValue('')
    }, [])

    const handleCustomAmountInputChange = useCallback((e) => {
        const value = e.target.value
        if (/^\d*\.?\d{0,2}$/.test(value)) {
            setCustomAmountValue(value)
        }
    }, [])

    useEffect(() => {
        const amountToSet = isCustomAmountSelected ? parsedCustomAmount : selectedAmountOption
        setAmount(isAmountValid && amountToSet !== null ? amountToSet : null)
    }, [isAmountValid, parsedCustomAmount, selectedAmountOption, isCustomAmountSelected, setAmount])

    const textColor = useMemo(() => {
        if (isCustomAmountSelected) {
            if (customAmountValue && (isBelowMinimum || isAboveMaximum)) {
                return 'red.500'
            }
            return 'gray.500'
        }
        return 'gray.500'
    }, [isCustomAmountSelected, customAmountValue, isBelowMinimum, isAboveMaximum])

    const renderHelperMessage = () => {
        if (isCustomAmountSelected) {
            if (customAmountValue && isAboveMaximum) {
                return (
                    <FormattedMessage
                        id="giftcert_max_amount"
                        defaultMessage="The maximum amount is ${maxAmount}"
                        values={{maxAmount: giftCardMaxAmount}}
                    />
                )
            }
            return (
                <FormattedMessage
                    id="giftcert_min_amount"
                    defaultMessage="The minimum amount is ${minAmount}"
                    values={{minAmount: giftCardMinAmount}}
                />
            )
        }
        return (
            <FormattedMessage
                id="giftcert_min_amount"
                defaultMessage="The minimum amount is ${minAmount}"
                values={{minAmount: giftCardMinAmount}}
            />
        )
    }

    return (
        <Stack mb={{base: 0, sm: 4}} direction="column">
            <Text fontSize="2xl" fontWeight="extrabold" my={4}>
                {'$' + `${finalDisplayAmount ? Number(finalDisplayAmount).toFixed(2) : '00.00'}`}
            </Text>
            <Flex
                align="center"
                gap={2}
                mb={2}
                justifyContent={{base: 'space-between', md: 'unset'}}
            >
                <Flex fontWeight="semibold" direction="row">
                    <Text color="orangeBaseColor">*</Text>
                    <FormattedMessage id="giftcert_select_amount" defaultMessage="Select amount" />
                </Flex>
                <Text fontSize="sm" color={textColor} ml={{base: '0', md: '12'}}>
                    {renderHelperMessage()}
                </Text>
            </Flex>
            <Flex gap={2} mb={4} wrap="wrap">
                {presetAmounts.map((value) => (
                    <Button
                        key={value}
                        onClick={() => handlePresetAmountClick(value)}
                        bg={selectedAmountOption === value ? 'black' : 'white'}
                        color={selectedAmountOption === value ? 'white' : 'black'}
                        border="2px solid"
                        borderRadius="8px"
                        _hover={{bg: 'black', color: 'white'}}
                    >
                        {'$' + `${value}`}
                    </Button>
                ))}
                <Button
                    onClick={() => handlePresetAmountClick('custom')}
                    bg={isCustomAmountSelected ? 'black' : 'white'}
                    color={isCustomAmountSelected ? 'white' : 'black'}
                    border="2px solid"
                    borderRadius="8px"
                    _hover={{bg: 'black', color: 'white'}}
                >
                    <FormattedMessage id="giftcert_custom" defaultMessage="Custom" />
                </Button>
            </Flex>
            {isCustomAmountSelected && (
                <Box>
                    <Text fontWeight="semibold" mb={1}>
                        <FormattedMessage
                            id="giftcert_enter_custom_amount"
                            defaultMessage="Enter custom amount"
                        />
                    </Text>
                    <Input
                        placeholder="Enter Amount"
                        value={customAmountValue}
                        onChange={handleCustomAmountInputChange}
                        type="text"
                        border="1px solid"
                        borderColor="black"
                        borderRadius="4px"
                        bgColor="white"
                        _focus={{borderColor: 'orangeBaseColor', boxShadow: 'none'}}
                    />
                </Box>
            )}
        </Stack>
    )
}

AmountSelector.propTypes = {
    setAmount: PropTypes.func.isRequired
}

export default AmountSelector
